import React, { useState, useEffect, useRef, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { makeStyles } from 'tss-react/mui';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Grid,
  Typography,
  Box,
  Alert,
  Card,
  CardContent,
  Stack,
  Chip,
  IconButton,
  useTheme
} from "@mui/material";
import {
  LocationOn as LocationOnIcon,
  Link as LinkIcon,
  Map as MapIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Google as GoogleIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { green } from "@mui/material/colors";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";

import { AuthContext } from "../../context/Auth/AuthContext";
import ToastSuccess from "../../toast/success/toastSuccess";
import ToastError from "../../toast/error/toastError";

const useStyles = makeStyles()((theme) => {
  return {
    root: {
      flexWrap: "wrap",
    },
    textField: {
      marginRight: theme.spacing(1),
      width: "100%",
    },

    btnWrapper: {
      position: "relative",
    },

    buttonProgress: {
      color: green[500],
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -12,
      marginLeft: -12,
    },
    textContainer: {
      width: "100%",
    },
  }
});

const CompanyAddressSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Muito Curto(a)!")
    .max(100, "Muito Longo(a)!")
    .required("Obrigatório"),
  address: Yup.string()
    .min(5, "Muito Curto(a)!")
    .max(500, "Muito Longo(a)!")
    .required("Obrigatório"),
  lat: Yup.number()
    .required("Latitude é obrigatória")
    .min(-90, "Latitude deve estar entre -90 e 90")
    .max(90, "Latitude deve estar entre -90 e 90"),
  lng: Yup.number()
    .required("Longitude é obrigatória")
    .min(-180, "Longitude deve estar entre -180 e 180")
    .max(180, "Longitude deve estar entre -180 e 180"),
  mapsUrl: Yup.string()
    .url("URL inválida")
    .required("Link do Google Maps é obrigatório"),
});

const CreateOrEditCompanyAddressModal = ({
  open,
  onClose,
  companyAddressId,
  initialValues,
  onSave,
}) => {
  const { classes } = useStyles();
  const theme = useTheme();  
  const isMounted = useRef(true);

  const { user: loggedInUser } = useContext(AuthContext);

  const initialState = {
    companyId: loggedInUser.companyId,
    name: "",
    address: "",
    lat: "",
    lng: "",
    mapsUrl: "",
  };

  const [companyAddress, setCompanyAddress] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [position, setPosition] = useState({});
  const [locationInfo, setLocationInfo] = useState({});
  const [mapLink, setMapLink] = useState("");
  const [resolvedUrl, setResolvedUrl] = useState("");
  const [error, setError] = useState("");
  const [extractedFromMap, setExtractedFromMap] = useState(false);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchCompanyAddress = async () => {
      if (initialValues) {
        setCompanyAddress((prevState) => {
          return { ...prevState, ...initialValues };
        });
      }

      if (!companyAddressId) return;

      try {
        const { data } = await api.get(`/companyAddresses/${companyAddressId}`);
        if (isMounted.current) {
          setCompanyAddress(data);
          
          // Se já tem coordenadas, configurar posição
          if (data.lat && data.lng) {
            setPosition({ lat: parseFloat(data.lat), lng: parseFloat(data.lng) });
            setLocationInfo({
              name: data.name,
              address: data.address,
              coordinates: `${data.lat}, ${data.lng}`
            });
            setExtractedFromMap(true);
          }
          
          // Se tem mapsUrl, configurar o link
          if (data.mapsUrl) {
            setMapLink(data.mapsUrl);
            setResolvedUrl(data.mapsUrl);
          }
        }
      } catch (err) {
        ToastError(err);
      }
    };

    fetchCompanyAddress();
  }, [companyAddressId, open, initialValues]);

  // Função para resolver URL usando o backend
  const resolveGoogleMapsUrl = async (url) => {
    try {
      const response = await api.get('/google/resolve-url', {
        params: { url }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao resolver URL:', error);
      throw error;
    }
  };

  // Função para processar links do Google Maps
  const processMapLink = async () => {
    if (!mapLink.trim()) {
      ToastError("Cole um link válido do Google Maps.");
      return;
    }

    setLoading(true);
    setError("");
    
    try {
      // Usar o backend para resolver a URL e extrair coordenadas
      const result = await resolveGoogleMapsUrl(mapLink.trim());
      
      setPosition({ lat: result.lat, lng: result.lng });
      setResolvedUrl(result.resolvedUrl || mapLink);
      const name = result.name || result.address?.split(',')[0] || "Localização do Google Maps";
      const address = result.address || `Coordenadas: ${result.lat.toFixed(6)}, ${result.lng.toFixed(6)}`;
      
      setLocationInfo({
        name: name,
        address: address,
        coordinates: `${result.lat.toFixed(6)}, ${result.lng.toFixed(6)}`
      });
      
      // Atualizar os campos do formulário
      setCompanyAddress(prev => ({
        ...prev,
        name: name,
        address: address,
        lat: result.lat,
        lng: result.lng,
        mapsUrl: result.resolvedUrl || mapLink
      }));
      
      setExtractedFromMap(true);
      
      toast.success("Localização extraída com sucesso!", {
        position: "bottom-right"
      });
    } catch (error) {
      console.error("Erro ao processar link:", error);
      const errorMessage = error.response?.data?.error || "Não foi possível extrair coordenadas do link. Verifique se é um link válido do Google Maps.";
      setError(errorMessage);
      ToastError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Função para gerar URL do Google Maps iframe
  const generateGoogleMapsEmbedUrl = (lat, lng) => {
    const isDarkMode = theme.palette.mode === 'dark';
    
    // Parâmetros base
    let url = `https://maps.google.com/maps?q=${lat},${lng}&hl=pt-BR&z=16&output=embed`;
    
    // Adicionar parâmetros de tema escuro
    if (isDarkMode) {
      url += '&theme=dark&style=feature:all|element:geometry|color:0x242f3e';
      url += '&style=feature:all|element:labels.text.stroke|color:0x242f3e';
      url += '&style=feature:all|element:labels.text.fill|color:0x746855';
      url += '&style=feature:administrative.locality|element:labels.text.fill|color:0xd59563';
      url += '&style=feature:poi|element:labels.text.fill|color:0xd59563';
      url += '&style=feature:poi.park|element:geometry|color:0x263c3f';
      url += '&style=feature:poi.park|element:labels.text.fill|color:0x6b9a76';
      url += '&style=feature:road|element:geometry|color:0x38414e';
      url += '&style=feature:road|element:geometry.stroke|color:0x212a37';
      url += '&style=feature:road|element:labels.text.fill|color:0x9ca5b3';
      url += '&style=feature:road.highway|element:geometry|color:0x746855';
      url += '&style=feature:road.highway|element:geometry.stroke|color:0x1f2835';
      url += '&style=feature:road.highway|element:labels.text.fill|color:0xf3d19c';
      url += '&style=feature:transit|element:geometry|color:0x2f3948';
      url += '&style=feature:transit.station|element:labels.text.fill|color:0xd59563';
      url += '&style=feature:water|element:geometry|color:0x17263c';
      url += '&style=feature:water|element:labels.text.fill|color:0x515c6d';
      url += '&style=feature:water|element:labels.text.stroke|color:0x17263c';
    }
    
    return url;
  };

  const handleClose = () => {
    onClose();
    setCompanyAddress(initialState);
    setPosition({});
    setLocationInfo({});
    setMapLink("");
    setResolvedUrl("");
    setError("");
    setExtractedFromMap(false);
  };

  const handleSaveCompanyAddress = async (values) => {
    try {
      if (companyAddressId) {
        await api.put(`/companyAddresses/${companyAddressId}`, values);
        handleClose();
      } else {
        const { data } = await api.post("/companyAddresses", values);
        if (onSave) {
          onSave(data);
        }
        handleClose();
      }

      ToastSuccess("Endereço salvo com sucesso!");

    } catch (err) {
      ToastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        scroll="body"
        BackdropProps={{
          style: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={2}>
              <BusinessIcon color="primary" sx={{ fontSize: 32 }} />
              <Typography variant="h5" fontWeight={600}>
                {companyAddressId ? "Editar Endereço" : "Adicionar Endereço"}
              </Typography>
            </Stack>
            
            <Stack direction="row" spacing={1}>
              <Chip 
                icon={<GoogleIcon />}
                label="Google Maps"
                size="small"
                color="primary"
                variant="outlined"
              />
              <IconButton onClick={handleClose} size="small">
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>
        </DialogTitle>

        <Formik
          initialValues={companyAddress}
          enableReinitialize={true}
          validationSchema={CompanyAddressSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveCompanyAddress(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            <Form>
              <Field
                style={{ display: "none" }}
                as={TextField}
                type="hidden"
                name="companyId"
              />
              
              <DialogContent dividers sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  {/* Link do Google Maps */}
                  <Grid item xs={12}>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Stack spacing={2}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <LinkIcon color="primary" />
                            <Typography variant="h6">Link do Google Maps *</Typography>
                            <Chip label="Obrigatório" color="error" size="small" variant="outlined" />
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            Cole um link do Google Maps para extrair automaticamente todos os dados necessários
                          </Typography>
                          
                          <Alert severity="warning" sx={{ borderRadius: 2 }}>
                            <Typography variant="body2">
                              <strong>⚠️ Campo obrigatório:</strong> Abra o Google Maps → Clique no local → Compartilhar → Copiar link
                            </Typography>
                          </Alert>
                          
                          <Alert severity="info" sx={{ borderRadius: 2 }}>
                            <Typography variant="body2">
                              <strong>💡 Importante:</strong> Todos os campos (nome, endereço, coordenadas e URL) são obrigatórios para salvar o endereço.
                            </Typography>
                          </Alert>
                          
                          {error && (
                            <Alert severity="error" sx={{ borderRadius: 2 }}>
                              {error}
                            </Alert>
                          )}
                          
                          <Stack direction="row" spacing={2}>
                            <TextField
                              fullWidth
                              placeholder="https://maps.app.goo.gl/1NaGCSJoXd4mi1xu8 ou https://maps.google.com/..."
                              value={mapLink}
                              onChange={(e) => setMapLink(e.target.value)}
                              size="small"
                              disabled={loading}
                              sx={{ 
                                '& .MuiOutlinedInput-root': { 
                                  borderRadius: 2 
                                } 
                              }}
                            />
                            <Button
                              variant="contained"
                              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LinkIcon />}
                              onClick={processMapLink}
                              disabled={loading || !mapLink.trim()}
                              sx={{ borderRadius: 2, minWidth: 120 }}
                            >
                              {loading ? "..." : "Extrair"}
                            </Button>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Formulário de Dados */}
                  <Grid item xs={12}>
                    <Card sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Stack spacing={3}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <BusinessIcon color="primary" />
                            <Typography variant="h6">Informações do Endereço *</Typography>
                            <Chip label="Todos obrigatórios" color="error" size="small" variant="outlined" />
                            {extractedFromMap && (
                              <Chip 
                                label="Extraído do Maps" 
                                color="success" 
                                variant="outlined"
                                icon={<CheckCircleIcon />}
                                size="small"
                              />
                            )}
                          </Stack>

                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Field
                                as={TextField}
                                label="Nome do Local *"
                                name="name"
                                autoFocus
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name}
                                variant="outlined"
                                margin="dense"
                                className={classes.textField}
                                fullWidth
                                value={values.name}
                                onChange={(e) => {
                                  setFieldValue('name', e.target.value);
                                  setCompanyAddress(prev => ({ ...prev, name: e.target.value }));
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Field
                                as={TextField}
                                label="Endereço Completo *"
                                name="address"
                                error={touched.address && Boolean(errors.address)}
                                helperText={touched.address && errors.address}
                                variant="outlined"
                                margin="dense"
                                className={classes.textField}
                                fullWidth
                                multiline
                                rows={2}
                                value={values.address}
                                onChange={(e) => {
                                  setFieldValue('address', e.target.value);
                                  setCompanyAddress(prev => ({ ...prev, address: e.target.value }));
                                }}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Field
                                as={TextField}
                                label="URL do Google Maps *"
                                name="mapsUrl"
                                error={touched.mapsUrl && Boolean(errors.mapsUrl)}
                                helperText={touched.mapsUrl && errors.mapsUrl}
                                variant="outlined"
                                margin="dense"
                                className={classes.textField}
                                fullWidth
                                value={values.mapsUrl}
                                onChange={(e) => {
                                  setFieldValue('mapsUrl', e.target.value);
                                  setCompanyAddress(prev => ({ ...prev, mapsUrl: e.target.value }));
                                }}
                                placeholder="https://maps.google.com/..."
                              />
                            </Grid>
                            
                            {/* Campos ocultos para lat e lng */}
                            <Field
                              as={TextField}
                              name="lat"
                              type="hidden"
                              style={{ display: "none" }}
                              value={values.lat}
                            />
                            <Field
                              as={TextField}
                              name="lng"
                              type="hidden"
                              style={{ display: "none" }}
                              value={values.lng}
                            />
                          </Grid>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Preview do Mapa */}
                  {(position.lat && position.lng) && (
                    <Grid item xs={12}>
                      <Card sx={{ borderRadius: 2 }}>
                        <CardContent>
                          <Stack spacing={2}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <MapIcon color="primary" />
                              <Typography variant="subtitle1" fontWeight={600}>
                                Preview do Mapa
                              </Typography>
                              <Chip label="Google Maps" size="small" variant="outlined" color="primary" />
                            </Stack>
                            
                            <Box sx={{ 
                              borderRadius: 2, 
                              overflow: 'hidden', 
                              boxShadow: 3, 
                              height: 300,
                              position: 'relative'
                            }}>
                              <iframe
                                src={generateGoogleMapsEmbedUrl(position.lat, position.lng)}
                                width="100%"
                                height="100%"
                                style={{ border: 0, pointerEvents: 'auto' }}
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                title="Google Maps Preview"
                                sandbox="allow-scripts"
                              />
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              
              <DialogActions sx={{ p: 3 }}>
                <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                  <Button
                    onClick={handleClose}
                    color="secondary"
                    disabled={isSubmitting || loading}
                    variant="outlined"
                    size="large"
                    sx={{ borderRadius: 2, minWidth: 120 }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    disabled={isSubmitting || loading || !values.name || !values.address || !values.lat || !values.lng || !values.mapsUrl}
                    variant="contained"
                    className={classes.btnWrapper}
                    size="large"
                    fullWidth
                    sx={{ borderRadius: 2 }}
                  >
                    {companyAddressId ? "Salvar Alterações" : "Adicionar Endereço"}
                    {(isSubmitting || loading) && (
                      <CircularProgress
                        size={24}
                        className={classes.buttonProgress}
                      />
                    )}
                  </Button>
                </Stack>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default CreateOrEditCompanyAddressModal;